import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  CreateEvaluationTestRequest,
  EvaluationProviderConfig,
  PromptMode
} from '../types/evaluation';
import { ProviderInfo } from '../types/provider';
import { EvaluationApiService } from '../services/evaluationApi';
import { ApiService } from '../services/apiService';
import { EvaluationProviderConfig as EvaluationProviderConfigComponent } from './EvaluationProviderConfig';

interface CreateEvaluationTestDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (testId: string) => void;
  testId?: string;
  mode?: 'create' | 'edit';
}

export const CreateEvaluationTestDialog = ({
  isOpen,
  onClose,
  onSuccess,
  testId,
  mode = 'create'
}: CreateEvaluationTestDialogProps) => {
  const [testName, setTestName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [promptMode, setPromptMode] = useState<PromptMode>('Fixed');
  const [fixedPrompt, setFixedPrompt] = useState('');
  const [executionLimit, setExecutionLimit] = useState<string>('');
  const [providerConfigs, setProviderConfigs] = useState<EvaluationProviderConfig[]>([
    { id: crypto.randomUUID(), name: '', maxOpinions: 5, maxPassagesPerDocument: 3, useReducer: false },
    { id: crypto.randomUUID(), name: '', maxOpinions: 5, maxPassagesPerDocument: 3, useReducer: false }
  ]);



  const [, setProviders] = useState<ProviderInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [cannotEditMessage, setCannotEditMessage] = useState<string | null>(null);

  const evaluationApi = EvaluationApiService.getInstance();
  const apiService = ApiService.getInstance();

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, testId]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Load providers
      const providersData = await apiService.getProviders();
      setProviders(providersData);

      // If editing, load the test data
      if (mode === 'edit' && testId) {
        const testData = await evaluationApi.getEvaluationTest(testId);
        if (!testData) {
          throw new Error('Test not found');
        }

        // Check if test can be edited
        if (!testData.canEdit) {
          setCannotEditMessage('This test cannot be edited because it has been executed');
          // Still populate the form for viewing, but it will be disabled
        }

        // Populate form with existing data
        setTestName(testData.name);
        setPurpose(testData.purpose);
        setPromptMode(testData.mode);
        setFixedPrompt(testData.fixedPrompt || '');
        setExecutionLimit(testData.executionLimit?.toString() || '');

        // Directly use the provider configs from the test
        setProviderConfigs(testData.providerConfigs);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
      console.error('Error loading data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!testName.trim()) {
      errors.testName = 'Test name is required';
    }

    if (!purpose.trim()) {
      errors.purpose = 'Purpose is required';
    }

    if (promptMode === 'Fixed' && !fixedPrompt.trim()) {
      errors.fixedPrompt = 'Fixed prompt is required in Fixed mode';
    }

    providerConfigs.forEach((config, index) => {
      if (!config.name.trim()) {
        errors[`provider_${index}_name`] = `Provider ${index + 1} name is required`;
      }
      // Check if at least one agent is configured
      const hasAnyAgent = config.orchestrator || config.researcher || config.reducer || config.writer || config.relevancechecker;
      if (!hasAnyAgent && !config.providerId) {
        errors[`provider_${index}_config`] = `Provider ${index + 1} must have at least one agent configured`;
      }
    });

    if (executionLimit && (parseInt(executionLimit) < 1 || isNaN(parseInt(executionLimit)))) {
      errors.executionLimit = 'Execution limit must be a positive number';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      if (mode === 'edit' && testId) {
        // Update existing test
        const request: CreateEvaluationTestRequest = {
          name: testName.trim(),
          purpose: purpose.trim(),
          providerConfigs: providerConfigs,
          mode: promptMode,
          fixedPrompt: promptMode === 'Fixed' ? fixedPrompt.trim() : undefined,
          executionLimit: executionLimit ? parseInt(executionLimit) : undefined
        };

        await evaluationApi.updateEvaluationTest(testId, request);
        onSuccess(testId);
        resetForm();
        onClose();
      } else {
        // Create new test
        const request: CreateEvaluationTestRequest = {
          name: testName.trim(),
          purpose: purpose.trim(),
          providerConfigs: providerConfigs,
          mode: promptMode,
          fixedPrompt: promptMode === 'Fixed' ? fixedPrompt.trim() : undefined,
          executionLimit: executionLimit ? parseInt(executionLimit) : undefined
        };

        const newTestId = await evaluationApi.createEvaluationTest(request);
        onSuccess(newTestId);
        resetForm();
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${mode} evaluation test`);
      console.error(`Error ${mode}ing test:`, err);
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setTestName('');
    setPurpose('');
    setPromptMode('Fixed');
    setFixedPrompt('');
    setExecutionLimit('');
    setProviderConfigs([
      { id: crypto.randomUUID(), name: '', maxOpinions: 5, maxPassagesPerDocument: 3, useReducer: false },
      { id: crypto.randomUUID(), name: '', maxOpinions: 5, maxPassagesPerDocument: 3, useReducer: false }
    ]);
    setValidationErrors({});
    setError(null);
  };

  const handleClose = () => {
    if (!isSaving) {
      resetForm();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div data-component="CreateEvaluationTestDialog" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            {mode === 'edit' ? 'Edit Evaluation Test' : 'Create Evaluation Test'}
          </h2>
          <button
            onClick={handleClose}
            disabled={isSaving}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading && (
            <div data-flow="loading" className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading...</p>
            </div>
          )}

          {cannotEditMessage && (
            <div data-flow="cannot-edit" className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-800">{cannotEditMessage}</p>
            </div>
          )}

          {error && (
            <div data-flow="error" className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {Object.keys(validationErrors).length > 0 && (
            <div data-flow="validation-errors" className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-yellow-800 font-medium mb-2">Please fix the following errors:</p>
              <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                {Object.values(validationErrors).map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {!isLoading && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Basic Information</h3>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="testName" className="block text-sm font-medium text-gray-700 mb-1">
                      Test Name *
                    </label>
                    <input
                      id="testName"
                      type="text"
                      value={testName}
                      onChange={(e) => setTestName(e.target.value)}
                      disabled={isSaving || !!cannotEditMessage}
                      placeholder="e.g., GPT-4 vs Claude Comparison"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 ${
                        validationErrors.testName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {validationErrors.testName && (
                      <p className="text-sm text-red-600 mt-1">{validationErrors.testName}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="purpose" className="block text-sm font-medium text-gray-700 mb-1">
                      Purpose *
                    </label>
                    <textarea
                      id="purpose"
                      value={purpose}
                      onChange={(e) => setPurpose(e.target.value)}
                      disabled={isSaving || !!cannotEditMessage}
                      placeholder="Describe the goal of this evaluation test..."
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 ${
                        validationErrors.purpose ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {validationErrors.purpose && (
                      <p className="text-sm text-red-600 mt-1">{validationErrors.purpose}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Prompt Configuration */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Prompt Configuration</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prompt Mode *
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="Fixed"
                          checked={promptMode === 'Fixed'}
                          onChange={(e) => setPromptMode(e.target.value as PromptMode)}
                          disabled={isSaving || !!cannotEditMessage}
                          className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Fixed (pre-configured prompt)</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="Open"
                          checked={promptMode === 'Open'}
                          onChange={(e) => setPromptMode(e.target.value as PromptMode)}
                          disabled={isSaving || !!cannotEditMessage}
                          className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Open (user provides prompt)</span>
                      </label>
                    </div>
                  </div>

                  {promptMode === 'Fixed' && (
                    <div data-flow="fixed-mode">
                      <label htmlFor="fixedPrompt" className="block text-sm font-medium text-gray-700 mb-1">
                        Fixed Prompt *
                      </label>
                      <textarea
                        id="fixedPrompt"
                        value={fixedPrompt}
                        onChange={(e) => setFixedPrompt(e.target.value)}
                        disabled={isSaving || !!cannotEditMessage}
                        placeholder="Enter the prompt that will be used for all executions..."
                        rows={4}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 ${
                          validationErrors.fixedPrompt ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {validationErrors.fixedPrompt && (
                        <p className="text-sm text-red-600 mt-1">{validationErrors.fixedPrompt}</p>
                      )}
                    </div>
                  )}

                  <div>
                    <label htmlFor="executionLimit" className="block text-sm font-medium text-gray-700 mb-1">
                      Execution Limit (optional)
                    </label>
                    <input
                      id="executionLimit"
                      type="number"
                      min="1"
                      value={executionLimit}
                      onChange={(e) => setExecutionLimit(e.target.value)}
                      disabled={isSaving || !!cannotEditMessage}
                      placeholder="Leave empty for unlimited"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 ${
                        validationErrors.executionLimit ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {validationErrors.executionLimit && (
                      <p className="text-sm text-red-600 mt-1">{validationErrors.executionLimit}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      A warning will be shown when this limit is reached, but execution can continue
                    </p>
                  </div>
                </div>
              </div>

              {/* Provider Configurations */}
              <EvaluationProviderConfigComponent
                configs={providerConfigs}
                onConfigsChange={setProviderConfigs}
                disabled={isSaving || !!cannotEditMessage}
                validationErrors={validationErrors}
                mode="multi"
              />

              {/* Footer - Now inside form */}
              <div className="pt-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSaving}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  {cannotEditMessage ? 'Close' : 'Cancel'}
                </button>
                {!cannotEditMessage && (
                  <button
                    type="submit"
                    disabled={isSaving || isLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isSaving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {mode === 'edit' ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      mode === 'edit' ? 'Update Test' : 'Create Test'
                    )}
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
